"use client";

import { c as _c } from "react/compiler-runtime";
import type { ComponentProps } from "react";
import { Button } from "@workspace/ui/components/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@workspace/ui/components/dropdown-menu";
import { Spinner } from "@workspace/ui/components/spinner";
import { Moon, Sun, SunMoon } from "lucide-react";
import { useTranslations } from "next-intl";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { useTheme } from "@/hooks/use-theme";
function ThemeProvider(t0) {
  const $ = _c(6);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== props) {
    t1 = <NextThemesProvider attribute="class" defaultTheme="system" enableSystem={true} disableTransitionOnChange={true} {...props}>{children}</NextThemesProvider>;
    $[3] = children;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function ThemeToggleElement() {
  const $ = _c(29);
  const {
    isMounted,
    selectedTheme
  } = useTheme();
  const t = useTranslations("Ui.theme");
  if (isMounted === false) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <Spinner />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    let t1;
    if ($[1] !== t) {
      t1 = t("label");
      $[1] = t;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== t1) {
      t2 = <>{t0}{t1}</>;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (selectedTheme === "light") {
    let t0;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <Sun />;
      $[5] = t0;
    } else {
      t0 = $[5];
    }
    let t1;
    if ($[6] !== t) {
      t1 = t("label");
      $[6] = t;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    let t2;
    if ($[8] !== t) {
      t2 = t("light");
      $[8] = t;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    let t3;
    if ($[10] !== t1 || $[11] !== t2) {
      t3 = <>{t0}{t1} ({t2})</>;
      $[10] = t1;
      $[11] = t2;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    return t3;
  }
  if (selectedTheme === "dark") {
    let t0;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <Moon />;
      $[13] = t0;
    } else {
      t0 = $[13];
    }
    let t1;
    if ($[14] !== t) {
      t1 = t("label");
      $[14] = t;
      $[15] = t1;
    } else {
      t1 = $[15];
    }
    let t2;
    if ($[16] !== t) {
      t2 = t("dark");
      $[16] = t;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    let t3;
    if ($[18] !== t1 || $[19] !== t2) {
      t3 = <>{t0}{t1} ({t2})</>;
      $[18] = t1;
      $[19] = t2;
      $[20] = t3;
    } else {
      t3 = $[20];
    }
    return t3;
  }
  let t0;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SunMoon />;
    $[21] = t0;
  } else {
    t0 = $[21];
  }
  let t1;
  if ($[22] !== t) {
    t1 = t("label");
    $[22] = t;
    $[23] = t1;
  } else {
    t1 = $[23];
  }
  let t2;
  if ($[24] !== t) {
    t2 = t("system");
    $[24] = t;
    $[25] = t2;
  } else {
    t2 = $[25];
  }
  let t3;
  if ($[26] !== t1 || $[27] !== t2) {
    t3 = <>{t0}{t1} ({t2})</>;
    $[26] = t1;
    $[27] = t2;
    $[28] = t3;
  } else {
    t3 = $[28];
  }
  return t3;
}
function ThemeTogglesElement() {
  const $ = _c(30);
  const {
    selectedTheme,
    setTheme
  } = useTheme();
  const t = useTranslations("Ui.theme");
  let t0;
  if ($[0] !== t) {
    t0 = t("label");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <DropdownMenuLabel>{t0}</DropdownMenuLabel>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== setTheme) {
    t2 = newTheme => setTheme(newTheme);
    $[4] = setTheme;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Sun />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t("light");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <DropdownMenuRadioItem value="light">{t3}{t4}</DropdownMenuRadioItem>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Moon />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t) {
    t7 = t("dark");
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <DropdownMenuRadioItem value="dark">{t6}{t7}</DropdownMenuRadioItem>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SunMoon />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t) {
    t10 = t("system");
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10) {
    t11 = <DropdownMenuRadioItem value="system">{t9}{t10}</DropdownMenuRadioItem>;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== selectedTheme || $[22] !== t11 || $[23] !== t2 || $[24] !== t5 || $[25] !== t8) {
    t12 = <DropdownMenuRadioGroup value={selectedTheme} onValueChange={t2}>{t5}{t8}{t11}</DropdownMenuRadioGroup>;
    $[21] = selectedTheme;
    $[22] = t11;
    $[23] = t2;
    $[24] = t5;
    $[25] = t8;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t1 || $[28] !== t12) {
    t13 = <DropdownMenuGroup>{t1}{t12}</DropdownMenuGroup>;
    $[27] = t1;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}
export function ThemeToggleSub() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DropdownMenuSubTrigger><ThemeToggleElement /></DropdownMenuSubTrigger>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DropdownMenuSub>{t0}<DropdownMenuPortal><DropdownMenuSubContent align="start"><ThemeTogglesElement /></DropdownMenuSubContent></DropdownMenuPortal></DropdownMenuSub>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function ThemeToggle() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DropdownMenuTrigger render={<Button variant="outline"><ThemeToggleElement /></Button>} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DropdownMenu>{t0}<DropdownMenuContent align="start"><ThemeTogglesElement /></DropdownMenuContent></DropdownMenu>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export { ThemeProvider };